import { liveQuery } from "dexie";
import { useCallback, useEffect, useState } from "react";

import { getGatewayBaseUrl } from "../config";
import { PosPaymentRuntimeGatewayClient } from "../gateway/payment-runtime-client";
import { readLocalEmployeeSession } from "../local-store/employee-session";
import {
  applyPaymentRuntimePayload,
  readPaymentRuntime,
  type LocalPaymentRuntime,
} from "../local-store/payment-runtime";
import type { TerminalCredentialState } from "../local-store/terminal-state";
import { useRuntimeAutoSync } from "../sync/use-runtime-auto-sync";

export type PaymentRuntimeStatus = "loading" | "local" | "syncing" | "ready" | "error";

const emptyRuntime: LocalPaymentRuntime = { initialized: false, payload: null };

export function usePosPaymentRuntime(terminal: TerminalCredentialState | null, online: boolean) {
  const identity = terminal?.identity ?? null;
  const deviceSecret = terminal?.deviceSecret ?? "";
  const scopeKey = identity
    ? `${identity.organization_id}:${identity.branch_id}:${identity.commerce_channel_id}:${identity.node_id}`
    : "";
  const [runtime, setRuntime] = useState<LocalPaymentRuntime>(emptyRuntime);
  const [status, setStatus] = useState<PaymentRuntimeStatus>("loading");
  const [runtimeError, setRuntimeError] = useState<string | null>(null);

  const sync = useCallback(async () => {
    if (!identity || !deviceSecret || !online) return;
    const baseUrl = getGatewayBaseUrl();
    if (!baseUrl) {
      setRuntimeError("ยังไม่ได้กำหนด MORE POS Gateway");
      setStatus("error");
      return;
    }

    const employeeSession = await readLocalEmployeeSession();
    if (!employeeSession?.sessionToken) return;

    setStatus("syncing");
    setRuntimeError(null);
    const client = new PosPaymentRuntimeGatewayClient({ baseUrl });
    const result = await client.load({
      deviceSecret,
      sessionToken: employeeSession.sessionToken,
    });

    if (!result.ok || !result.data) {
      const local = await readPaymentRuntime(identity);
      setRuntime(local);
      setRuntimeError(result.error ?? "POS_PAYMENT_RUNTIME_LOAD_FAILED");
      setStatus(local.initialized ? "local" : "error");
      return;
    }

    await applyPaymentRuntimePayload(identity, result.data);
    const latest = await readPaymentRuntime(identity);
    setRuntime(latest);
    setRuntimeError(null);
    setStatus("ready");
  // identity may be rehydrated; scopeKey is the stable runtime dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceSecret, online, scopeKey]);

  useEffect(() => {
    if (!identity || !scopeKey) {
      setRuntime(emptyRuntime);
      setStatus("loading");
      return;
    }
    const subscription = liveQuery(() => readPaymentRuntime(identity)).subscribe({
      next: (value) => {
        setRuntime(value);
        setStatus((current) => current === "syncing" ? current : value.initialized ? "local" : "loading");
      },
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useRuntimeAutoSync({
    enabled: Boolean(scopeKey && online),
    sync,
    triggerKey: scopeKey,
  });

  return { runtime, status, runtimeError, sync };
}
